/**
 * Accounting Recurring transactions page composer.
 *
 * Print refactor (2026-10-22, fe/recurring-print-header):
 *   Print HTML + CSS + cell rendering now live in
 *   ./print/printTemplate.ts and ./print/printRecurringList.ts.
 *   This file drops the three inline functions it used to carry
 *   (escapeHtml, printCellContent, printRecurringList) and just
 *   calls the orchestrator. New letterhead design renders inside
 *   printTemplate.ts.
 *
 * Earlier hotfix note (kept): useAppLayoutData was removed because
 * backend /me/ was 404ing in a retry loop. Company name reads from
 * localStorage directly. getCompanyName() returns empty when nothing
 * is stored, which hides the letterhead's left company block
 * entirely on the printed output (no placeholder text).
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { useSearchParams } from "react-router-dom";
import { useBreakpoint } from "../../../hooks/useBreakpoint";
import { useRecurring } from "../../../hooks/useRecurring";
import { useColumnPreferences } from "../../../hooks/useColumnPreferences";
import type {
  RecurringTemplate,
  RecurringSortField,
} from "./recurringTypes";
import {
  filterRecurring,
  sortRecurring,
  formatAmount,
} from "./recurringUtils";
import {
  MODE_LABELS,
  TXN_TYPE_LABELS,
  MOCK_RECURRING_TEMPLATES,
} from "./recurringMockData";
import {
  DEFAULT_VISIBLE_COLUMNS,
  REQUIRED_COLUMNS,
  RECURRING_COLUMNS_STORAGE_KEY,
  type ColumnKey,
} from "./recurringColumns";
import {
  RecurringHeader,
  type RecurringViewMode,
  urlModeToFilterMode,
} from "./components/RecurringHeader";
import { RecurringToolbar } from "./components/RecurringToolbar";
import { RecurringTable, RecurringTableHead } from "./components/RecurringTable";
import { RecurringCard } from "./components/RecurringCard";
import { RecurringEmptyState } from "./components/RecurringEmptyState";
import {
  FilterModal,
  type TemplateTypeFilter,
  type TransactionTypeFilter,
} from "./components/FilterModal";
import { RowActionsMenu } from "./components/RowActionsMenu";
import {
  ConfirmModal,
  type ConfirmModalVariant,
  type ConfirmModalIcon,
} from "./components/ConfirmModal";
import { CustomizeColumnsPopover } from "./components/CustomizeColumnsPopover";
import { RecurringBuilder } from "./builder/RecurringBuilder";
import { BuilderBasics } from "./builder/BuilderBasics";
import { BuilderTxn } from "./builder/BuilderTxn";
import { BuilderSchedule } from "./builder/BuilderSchedule";
import { BuilderPreview } from "./builder/BuilderPreview";
import { useRecurringBuilder } from "./builder/useRecurringBuilder";
import type { BuilderContextOptions } from "./builder/builderTypes";
import { printRecurringList } from "./print/printRecurringList";

export { RecurringHeader };

const TOAST_DURATION_MS = 3200;

/**
 * Company name for the print header. Reads from localStorage.
 * Returns empty string if no company name is stored (print header
 * then omits the left letterhead block entirely).
 */
function getCompanyName(): string {
  try {
    return (
      localStorage.getItem("company_name") ||
      localStorage.getItem("companyName") ||
      ""
    );
  } catch {
    return "";
  }
}

const BUILDER_OPTIONS: BuilderContextOptions = {
  suppliers: [
    { id: "sup-king-st", name: "King Street Properties Ltd" },
    { id: "sup-enbridge", name: "Enbridge Gas Inc" },
    { id: "sup-epcor", name: "EPCOR Utilities" },
    { id: "sup-shopify", name: "Shopify Inc" },
    { id: "sup-rogers", name: "Rogers Communications" },
    { id: "sup-costco", name: "Costco Wholesale" },
    { id: "sup-wcb", name: "WCB Alberta" },
    { id: "sup-cra", name: "CRA GST/HST" },
    { id: "sup-atb", name: "ATB Financial" },
    { id: "sup-ford-credit", name: "Ford Credit Canada" },
    { id: "sup-intact", name: "Intact Insurance" },
    { id: "sup-sgi", name: "SGI Canada" },
    { id: "sup-google", name: "Google Cloud" },
    { id: "sup-slack", name: "Slack Technologies" },
    { id: "sup-zoom", name: "Zoom Video" },
    { id: "sup-godaddy", name: "GoDaddy Canada" },
    { id: "sup-spotless", name: "Spotless Cleaners Ltd" },
    { id: "sup-national-leasing", name: "National Leasing" },
    { id: "sup-city-edmonton", name: "City of Edmonton" },
    { id: "sup-edm-chamber", name: "Edmonton Chamber" },
  ],
  customers: [
    { id: "cust-riverside", name: "Riverside Clinic" },
    { id: "cust-acme", name: "Acme Industries" },
    { id: "cust-greenway", name: "Greenway Logistics" },
    { id: "cust-brightcare", name: "BrightCare Home Healthcare" },
  ],
  accounts: [
    { id: "acc-rent-expense", name: "Rent expense" },
    { id: "acc-wages-expense", name: "Wages expense" },
    { id: "acc-utilities", name: "Utilities" },
    { id: "acc-internet-and-phone", name: "Internet and phone" },
    { id: "acc-office-expense", name: "Office expense" },
    { id: "acc-office-supplies", name: "Office supplies" },
    { id: "acc-software-subscriptions", name: "Software subscriptions" },
    { id: "acc-insurance", name: "Insurance" },
    { id: "acc-dues-and-subscriptions", name: "Dues and subscriptions" },
    { id: "acc-licenses-and-permits", name: "Licenses and permits" },
    { id: "acc-lease-expense", name: "Lease expense" },
    { id: "acc-vehicle-lease-expense", name: "Vehicle lease expense" },
    { id: "acc-loan-interest-expense", name: "Loan interest expense" },
    { id: "acc-workers-comp-payable", name: "Workers comp payable" },
    { id: "acc-gst-payable", name: "GST payable" },
    { id: "acc-pst-payable", name: "PST payable" },
    { id: "acc-sales-income", name: "Sales income" },
    { id: "acc-owner-equity", name: "Owner equity" },
    { id: "acc-dividends-payable", name: "Dividends payable" },
    { id: "acc-bonus-payable", name: "Bonus payable" },
    { id: "acc-accumulated-depreciation", name: "Accumulated depreciation" },
    { id: "acc-rbc-savings", name: "RBC Savings" },
    { id: "acc-rbc-chequing", name: "RBC Chequing" },
    { id: "acc-td-visa", name: "TD Visa" },
  ],
};

interface ConfirmState {
  variant: ConfirmModalVariant;
  icon: ConfirmModalIcon;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  statValue?: string;
  statLabel?: string;
  footerNote?: ReactNode;
  onConfirm: () => void;
}

export default function RecurringPage() {
  const { isMobile } = useBreakpoint();
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState("");
  const [templateType, setTemplateType] = useState<TemplateTypeFilter>("all");
  const [transactionType, setTransactionType] =
    useState<TransactionTypeFilter>("all");
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuTemplate, setMenuTemplate] = useState<RecurringTemplate | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<RecurringSortField>("nextDate");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [customizeOpen, setCustomizeOpen] = useState(false);

  const settingsBtnRef = useRef<HTMLButtonElement>(null);

  const columnPrefs = useColumnPreferences<ColumnKey>({
    storageKey: RECURRING_COLUMNS_STORAGE_KEY,
    defaults: DEFAULT_VISIBLE_COLUMNS,
    required: REQUIRED_COLUMNS,
  });

  const {
    data,
    updateTemplate,
    deleteTemplate,
    togglePause,
    runNow,
    skipNext,
    endTemplate,
    createTemplate,
  } = useRecurring();

  const builder = useRecurringBuilder({
    onSave: (template) => {
      const existed = data.some((t) => t.id === template.id);
      if (!existed) {
        createTemplate(template);
        setToast(`Created "${template.name}".`);
      } else {
        updateTemplate(template.id, template);
        setToast(`Saved "${template.name}".`);
      }
    },
    onDelete: (id) => {
      const t = data.find((t) => t.id === id);
      deleteTemplate(id);
      setToast(`Deleted "${t?.name ?? "template"}".`);
    },
  });

  const { openForCreate, closeImmediate, isDirty, editingTemplate } = builder;

  useEffect(() => {
    if (searchParams.get("action") === "new") {
      openForCreate();
      const next = new URLSearchParams(searchParams);
      next.delete("action");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, openForCreate]);

  const handleDrawerClose = useCallback(() => {
    if (!isDirty) {
      closeImmediate();
      return;
    }
    const templateName = editingTemplate?.name ?? "this template";
    setConfirm({
      variant: "danger",
      icon: "delete",
      title: "Discard unsaved changes?",
      description: (
        <>
          You have unsaved changes to <strong>{templateName}</strong>. If you
          close now, those changes will be lost and the template stays as it was.
        </>
      ),
      confirmLabel: "Discard changes",
      footerNote: "Click Keep editing to go back and save them.",
      onConfirm: () => {
        setConfirm(null);
        closeImmediate();
      },
    });
  }, [isDirty, editingTemplate, closeImmediate]);

  const viewMode = (searchParams.get("mode") as RecurringViewMode | null) ?? "reminder";

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), TOAST_DURATION_MS);
    return () => window.clearTimeout(t);
  }, [toast]);

  const visible = useMemo(() => {
    const modeFilter = urlModeToFilterMode(viewMode);
    const byMode = data.filter((t) => t.mode === modeFilter);
    const filtered = filterRecurring(byMode, {
      search,
      templateType,
      transactionType,
    });
    return sortRecurring(filtered, sortBy, sortDirection);
  }, [data, viewMode, search, templateType, transactionType, sortBy, sortDirection]);

  const hasActiveFilters = templateType !== "all" || transactionType !== "all";

  const filterSummary = useMemo(() => {
    const parts: string[] = [];
    if (templateType !== "all") parts.push(MODE_LABELS[templateType]);
    if (transactionType !== "all") parts.push(TXN_TYPE_LABELS[transactionType]);
    return parts.length === 0 ? "All" : parts.join(" + ");
  }, [templateType, transactionType]);

  const handleSortChange = useCallback(
    (field: RecurringSortField) => {
      if (sortBy === field) {
        setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortBy(field);
        setSortDirection("asc");
      }
    },
    [sortBy],
  );

  const handleEdit = useCallback(
    (template: RecurringTemplate) => builder.openForEdit(template),
    [builder],
  );

  const handleDuplicate = useCallback(
    (template: RecurringTemplate) => {
      builder.openForCreate();
      builder.updateDraft({
        name: `${template.name} (copy)`,
        recurringMode: template.mode,
        txnType: template.txnType,
        accountId: template.transaction.accountId,
        accountName: template.transaction.accountName,
        amount: String(template.transaction.amount),
        currency: template.transaction.currency,
        memo: template.transaction.memo ?? "",
      });
    },
    [builder],
  );

  const handleRunNow = useCallback(
    (template: RecurringTemplate) => {
      setConfirm({
        variant: "info",
        icon: "run",
        title: "Run this recurring template now?",
        description: (
          <>
            Posts <strong>{template.name}</strong> immediately as a transaction,
            independent of the schedule. The next scheduled run on{" "}
            <strong>{template.nextDate ?? "its next date"}</strong> still fires as normal.
          </>
        ),
        confirmLabel: "Post transaction",
        statValue: `$${formatAmount(template.transaction.amount)}`,
        statLabel: `will post to ${template.transaction.accountName}`,
        footerNote:
          "This creates a new transaction in your ledger. You can delete it later from Bank transactions.",
        onConfirm: () => {
          runNow(template.id);
          setConfirm(null);
          setToast(`Posted "${template.name}" to ${template.transaction.accountName}.`);
        },
      });
    },
    [runNow],
  );

  const handleSkipNext = useCallback(
    (template: RecurringTemplate) => {
      skipNext(template.id);
      setToast(`Skipped next occurrence of "${template.name}".`);
    },
    [skipNext],
  );

  const handleTogglePause = useCallback(
    (template: RecurringTemplate) => {
      const nextStatus = template.status === "active" ? "paused" : "active";
      togglePause(template.id);
      setToast(
        nextStatus === "paused"
          ? `Paused "${template.name}".`
          : `Resumed "${template.name}".`,
      );
    },
    [togglePause],
  );

  const handleEnd = useCallback(
    (template: RecurringTemplate) => {
      setConfirm({
        variant: "danger",
        icon: "end",
        title: "End this recurring?",
        description: (
          <>
            <strong>{template.name}</strong> will stop firing. Past transactions it
            already posted stay in your ledger (nothing changes there).
          </>
        ),
        confirmLabel: "End recurring",
        footerNote: "To stop temporarily without ending it, pause instead.",
        onConfirm: () => {
          endTemplate(template.id);
          setConfirm(null);
          setToast(`Ended "${template.name}".`);
        },
      });
    },
    [endTemplate],
  );

  const handleDelete = useCallback(
    (template: RecurringTemplate) => {
      setConfirm({
        variant: "danger",
        icon: "delete",
        title: "Delete this recurring template?",
        description: (
          <>
            <strong>{template.name}</strong> will no longer post new transactions.
            Past transactions it already posted stay in your ledger (nothing changes there).
          </>
        ),
        confirmLabel: "Delete template",
        footerNote:
          "To stop future posts without losing the template, pause it instead.",
        onConfirm: () => {
          deleteTemplate(template.id);
          setConfirm(null);
          setToast(`Deleted "${template.name}".`);
        },
      });
    },
    [deleteTemplate],
  );

  const handleOpenMenu = useCallback(
    (template: RecurringTemplate, anchor: HTMLElement) => {
      setMenuTemplate(template);
      setMenuAnchor(anchor);
    },
    [],
  );

  const handleCloseMenu = useCallback(() => {
    setMenuAnchor(null);
    setMenuTemplate(null);
  }, []);

  const viewModeLabel = useMemo(
    () =>
      viewMode === "reminder"
        ? "Reminder List"
        : viewMode === "scheduled"
          ? "Scheduled"
          : "Unscheduled",
    [viewMode],
  );

  const handlePrintClick = useCallback(() => {
    printRecurringList(visible, viewModeLabel, getCompanyName(), columnPrefs.visible);
  }, [visible, viewModeLabel, columnPrefs.visible]);

  const handleCustomizeClick = useCallback(() => {
    setCustomizeOpen(true);
  }, []);

  const handleApplyFilter = useCallback(
    (nextTemplate: TemplateTypeFilter, nextTxn: TransactionTypeFilter) => {
      setTemplateType(nextTemplate);
      setTransactionType(nextTxn);
    },
    [],
  );

  const totalDataCount = MOCK_RECURRING_TEMPLATES.length;

  return (
    <div style={pageStyle}>
      {totalDataCount === 0 ? (
        <RecurringEmptyState />
      ) : (
        <>
          <div style={stickyStackStyle}>
            <RecurringToolbar
              searchValue={search}
              onSearchChange={setSearch}
              onFilterClick={() => setFilterModalOpen(true)}
              filterSummary={filterSummary}
              hasActiveFilters={hasActiveFilters}
              onPrintClick={handlePrintClick}
              onCustomizeClick={handleCustomizeClick}
              settingsButtonRef={settingsBtnRef}
            />
            {!isMobile && (
              <RecurringTableHead
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
                visibleColumns={columnPrefs.visible}
              />
            )}
          </div>

          {visible.length === 0 ? (
            <div style={noMatchStyle}>
              No recurring transactions matching the criteria.
            </div>
          ) : isMobile ? (
            <div>
              {visible.map((template) => (
                <RecurringCard
                  key={template.id}
                  template={template}
                  onTogglePause={() => handleTogglePause(template)}
                  onOpenMenu={(anchor) => handleOpenMenu(template, anchor)}
                />
              ))}
            </div>
          ) : (
            <RecurringTable
              templates={visible}
              totalCount={data.length}
              onOpenMenu={handleOpenMenu}
              visibleColumns={columnPrefs.visible}
            />
          )}
        </>
      )}

      <FilterModal
        isOpen={filterModalOpen}
        templateType={templateType}
        transactionType={transactionType}
        onApply={handleApplyFilter}
        onClose={() => setFilterModalOpen(false)}
      />

      {menuTemplate && (
        <RowActionsMenu
          isOpen={menuAnchor !== null}
          template={menuTemplate}
          anchorEl={menuAnchor}
          onClose={handleCloseMenu}
          onEdit={handleEdit}
          onDuplicate={handleDuplicate}
          onRunNow={handleRunNow}
          onSkipNext={handleSkipNext}
          onTogglePause={handleTogglePause}
          onEnd={handleEnd}
          onDelete={handleDelete}
        />
      )}

      {confirm && (
        <ConfirmModal
          isOpen={true}
          variant={confirm.variant}
          icon={confirm.icon}
          title={confirm.title}
          description={confirm.description}
          confirmLabel={confirm.confirmLabel}
          statValue={confirm.statValue}
          statLabel={confirm.statLabel}
          footerNote={confirm.footerNote}
          onConfirm={confirm.onConfirm}
          onClose={() => setConfirm(null)}
        />
      )}

      <CustomizeColumnsPopover
        isOpen={customizeOpen}
        anchorEl={settingsBtnRef.current}
        visible={columnPrefs.visible}
        onToggle={columnPrefs.toggle}
        onReset={columnPrefs.reset}
        onClose={() => setCustomizeOpen(false)}
      />

      <RecurringBuilder
        isOpen={builder.isOpen}
        mode={builder.editingTemplate ? "edit" : "create"}
        isValid={builder.isValid}
        isSaving={builder.isSaving}
        editingTemplate={builder.editingTemplate}
        onClose={handleDrawerClose}
        onSubmit={builder.submit}
        onDelete={builder.requestDelete}
      >
        <BuilderBasics
          draft={builder.draft}
          errors={builder.errors}
          updateDraft={builder.updateDraft}
        />
        <BuilderTxn
          draft={builder.draft}
          errors={builder.errors}
          updateDraft={builder.updateDraft}
          options={BUILDER_OPTIONS}
        />
        <BuilderSchedule
          draft={builder.draft}
          errors={builder.errors}
          updateDraft={builder.updateDraft}
        />
        <BuilderPreview draft={builder.draft} />
      </RecurringBuilder>

      {toast && <Toast message={toast} />}
    </div>
  );
}

function Toast({ message }: { message: string }) {
  return (
    <div style={toastStyle} role="status" aria-live="polite">
      {message}
    </div>
  );
}

const pageStyle: CSSProperties = {
  width: "100%",
  maxWidth: "100%",
  margin: 0,
  padding: 0,
  background: "#ffffff",
};

const stickyStackStyle: CSSProperties = {
  position: "sticky",
  top: 0,
  zIndex: 15,
  background: "#ffffff",
  boxShadow: "0 1px 0 #E7EAF0",
};

const noMatchStyle: CSSProperties = {
  padding: "60px 32px",
  textAlign: "center",
  fontSize: 13.5,
  color: "#3A4A52",
  fontStyle: "italic",
};

const toastStyle: CSSProperties = {
  position: "fixed",
  bottom: 24,
  left: "50%",
  transform: "translateX(-50%)",
  background: "#0E1A1A",
  color: "#ffffff",
  padding: "10px 16px",
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 500,
  boxShadow: "0 8px 20px -4px rgba(14, 26, 26, 0.28)",
  zIndex: 1100,
};